import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { shouldShowInDevelopmentFeature } from '@vimeo/query-feature-flag';
import { useHasMounted } from '@vimeo/ui/hooks/use-has-mounted';

import { createPlayerInterface } from '../utils/CreatePlayerInterface';
import { propsToEvent } from './constants';
import type { EmbedPlayerAPI, PlayerEventHandler, PlayerEventName } from './Player.types';
import type { EmbedPlayerProps, CallbackPropsName } from './types';

export type Ref = HTMLIFrameElement;

const loggingFlag = shouldShowInDevelopmentFeature('EMBED_PLAYER_DEBUG', true);

/**
 * EmbedPlayer is a Iframe player embed with support for Vimeo Player API.
 * Then to initialize Player API once the iframe element has mounted.
 *
 * Player API only initialize once until the EmbedPlayer is unmounted:
 * - add `key` to EmbedPlayer can force re-mounting the component and re-initialize Player API when key changes
 * - Player API initialization is optional, default to enabled, props.disableAPI defaults to false
 * - When Player API script is not detected on the page or props.disableAPI set to true, Player API won't initialize
 * - Once Player API is initialized, props.src update will trigger `playerAPI.loadVideo` to switch video source
 *   If Player API is not initialized, the iframe.src will be updated directly by props.src
 *
 * EmbedPlayer provides a set of on- prefixed callback props to attach event listeners to Player API.
 * And a `onPlayerAPIReady` callback to expose the Player API instance to parent component.
 */
export const EmbedPlayer = React.forwardRef<Ref, EmbedPlayerProps>((props, ref) => {
  const {
    src = '',
    configUrl = '',
    title = 'Vimeo Player',
    style,
    disableAPI = false,
    onPlayerAPIReady,
    ...callbackProps
  } = props;

  const [isAPISupported, setIsAPISupported] = useState(!disableAPI);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  /**
   * Initialize Player API
   * Ideally this side effect will only run once after the iframe element has mounted.
   * We want to keep this initialization effect deps array minimal to prevent re-initialization on re-render.
   * To move other dependencies out of the deps array, we use `isPlayerReady` as a signal to trigger other side effect to attach event listeners below.
   */
  const hasMounted = useHasMounted();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerAPIRef = useRef<EmbedPlayerAPI | null>(null);

  useEffect(() => {
    /** bypass SSR and page remount */
    if (!hasMounted || typeof window === 'undefined') return;

    if (!iframeRef.current) return;

    /** bypass Player API not enabled */
    if (disableAPI || !window.Vimeo?.Player) {
      setIsAPISupported(false);
      return;
    }

    let isMounted = true;
    let playerAPI: EmbedPlayerAPI | null = null;

    try {
      /** Player only needs to initialize once */
      playerAPI = new window.Vimeo.Player(iframeRef.current);

      const wrappedPlayer = createPlayerInterface(playerAPI);

      playerAPI.on('loaded', () => {
        if (isMounted) setIsVideoLoaded(true);
      });

      if (isMounted) {
        playerAPIRef.current = wrappedPlayer;
        loggingFlag && console.log('DEBUG:: Player API initialized.');
      }
    } catch (error) {
      console.error('Error initializing player:', error);
      if (isMounted) setIsAPISupported(false);
    }

    return () => {
      isMounted = false;

      if (playerAPI) {
        playerAPI.off('loaded');
      }

      playerAPIRef.current = null;
    };
  }, [hasMounted, disableAPI]);

  /**
   * Switch video source
   * when playerAPI initialized we use `playerAPI.loadVideo` to handle `props.src` change
   * `playerAPI.loadVideo` is designed to load new videos in the same embed.
   *
   * IMPORTANT NOTE:
   * Currently there is a signature related issue, we have to use `configUrl` instead of `src` to switch a new video source.
   * This effect body logic shouldn't be triggered on first video load; first video load is directly from iframe.src and that's before player API initialized.
   */
  const srcRef = useRef(src);

  useEffect(() => {
    const playerAPI = playerAPIRef.current;

    if (!playerAPI || !configUrl) return;

    setIsVideoLoaded(false);
    playerAPI.loadVideo(configUrl).then(() => setIsVideoLoaded(true));
  }, [configUrl]);

  /**
   * onPlayerAPIReady callback to expose Player API to parent component
   */
  useEffect(() => {
    const playerAPI = playerAPIRef.current;

    if (isVideoLoaded && playerAPI && onPlayerAPIReady) {
      loggingFlag && console.log('DEBUG:: Player API Ready.');
      onPlayerAPIReady(playerAPI);
    }
  }, [isVideoLoaded, onPlayerAPIReady]);

  /**
   * Add/Remove "on-" prefixed callback props to Player API event listeners
   */
  const eventCallbacks = useMemo(() => {
    let initialArray: Array<{
      eventName: PlayerEventName;
      /**
       * Intentionally make it less sensitive, or it will be hard to iterate over the map
       * This is safe as we very strictly typed all the "on-" prefixed callback props in `EmbedPlayerProps`
       */
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      callback: PlayerEventHandler<any>;
    }> = [];

    return Object.keys(callbackProps).reduce((eventMap, propName) => {
      const callback = callbackProps[propName as CallbackPropsName];
      const eventName = propsToEvent[propName as CallbackPropsName];

      callback && eventName && eventMap.push({ eventName, callback });

      return eventMap;
    }, initialArray);
  }, [callbackProps]);

  useEffect(() => {
    const playerAPI = playerAPIRef.current;

    if (isVideoLoaded && playerAPI) {
      eventCallbacks.forEach(({ eventName, callback }) => {
        playerAPI.on(eventName, callback);
      });
    }

    return () => {
      if (!playerAPI || !isVideoLoaded) return;

      eventCallbacks.forEach(({ eventName }) => {
        playerAPI.off(eventName);
      });
    };
  }, [isVideoLoaded, eventCallbacks]);

  useImperativeHandle(ref, () => iframeRef.current as Ref, []);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      src={isAPISupported ? srcRef.current : src}
      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
      style={{ width: '100%', height: '100%', border: '0', ...style }}
      data-embed-url={src}
      referrerPolicy="unsafe-url"
    />
  );
});
